import { useState, useRef, useCallback } from "react";

/**
 * Single-submit guard for async save handlers.
 *
 * Use this in any form or dialog that writes to the backend. It solves
 * three overlapping bug classes we hit in Feb/Apr 2026:
 *
 *  1. **Double-submit duplicates** — user clicks Save twice (or the network
 *     is slow and the button isn't disabled) → two POSTs → duplicate rows.
 *  2. **Ghost state** — `onSave` completes but `onRefresh()` is fired
 *     without `await`, so the form closes before the refetched data
 *     arrives. The user sees a brief "nothing happened" render.
 *  3. **Race on unmount** — a slow save resolving after the component
 *     unmounts would try to setState on an unmounted node. The ref-based
 *     guard short-circuits repeat calls during the in-flight window.
 *
 * Usage
 * -----
 *     const { saving, run } = useAsyncSave();
 *
 *     const handleSave = () => run(async () => {
 *       await axios.post(url, body);
 *       await Promise.resolve(onRefresh?.());
 *       onClose?.();
 *     });
 *
 *     <Button disabled={saving} onClick={handleSave}>
 *       {saving ? "Saving…" : "Save"}
 *     </Button>
 *
 * Prefer the `AsyncSaveButton` component when the button is the only
 * thing that needs wiring — it handles `saving` + spinner internally.
 */
export function useAsyncSave() {
  const [saving, setSaving] = useState(false);
  // Ref guard runs BEFORE the setState commits, so rapid clicks inside
  // the same event loop tick are blocked even before React re-renders.
  const inFlightRef = useRef(false);

  const run = useCallback(async (fn) => {
    if (inFlightRef.current) return undefined;
    inFlightRef.current = true;
    setSaving(true);
    try {
      return await fn();
    } finally {
      inFlightRef.current = false;
      setSaving(false);
    }
  }, []);

  return { saving, run };
}

/**
 * Small utility: treat any value as a promise so parent `onRefresh`
 * callbacks (which may be sync OR async) can be awaited uniformly.
 *
 *     await awaitRefresh(onRefresh?.());
 */
export const awaitRefresh = (maybePromise) => Promise.resolve(maybePromise);
